import { useMemo } from 'react';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';

/**
 * Batch-resolves the verification state of a list of IG handles for the
 * <VerifiedBadge> shown next to them. One RPC round trip for the whole list
 * (get_handle_verification) — so a card/list RPC never needs its own signature
 * changed just to carry the flag.
 *
 * Returns a lookup: verif(handle) → { verified, claimed }. Matching is
 * case-insensitive and ignores a leading '@', so pass handles however you have
 * them. Unknown handles resolve to { verified:false, claimed:false } (no badge).
 *
 *   const verif = useHandleVerification(rows.map(r => r.handle));
 *   <VerifiedBadge {...verif(r.handle)} />
 */
export type HandleVerif = { verified: boolean; claimed: boolean };

const EMPTY: HandleVerif = { verified: false, claimed: false };
const norm = (h: string | null | undefined) => (h ?? '').replace(/^@+/, '').toLowerCase();

export function useHandleVerification(handles: Array<string | null | undefined>) {
  // Stable, de-duplicated, normalized key so the query only refires when the
  // actual set of handles changes (not on every parent render).
  const uniq = useMemo(() => {
    const set = new Set<string>();
    for (const h of handles) { const n = norm(h); if (n) set.add(n); }
    return [...set].sort();
  }, [handles]);

  const { data } = useSupabaseQuery<Array<{ handle: string; verified: boolean; claimed: boolean }>>(
    ['handle-verification', uniq],
    async () => {
      if (uniq.length === 0) return { data: [], error: null };
      return supabase.rpc('get_handle_verification', { p_handles: uniq });
    },
    { enabled: uniq.length > 0, staleTime: 5 * 60 * 1000 },
  );

  const map = useMemo(() => {
    const m = new Map<string, HandleVerif>();
    for (const row of data ?? []) m.set(norm(row.handle), { verified: !!row.verified, claimed: !!row.claimed });
    return m;
  }, [data]);

  return useMemo(() => (handle: string | null | undefined): HandleVerif => map.get(norm(handle)) ?? EMPTY, [map]);
}
